import { Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler, Tooltip, Legend } from 'chart.js';

/* --------------------------------------------------
                Register Chart Components
-------------------------------------------------- */
Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler, Tooltip, Legend);

/* --------------------------------------------------
                Utilities
-------------------------------------------------- */
const hexToRgba = (hex, alpha = 1) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Memoize patterns to avoid recreating canvases
const patternCache = new Map();
const createStripedPattern = (color) => {
  if (patternCache.has(color)) return patternCache.get(color);

  const pattern = document.createElement('canvas');
  const ctx = pattern.getContext('2d');
  pattern.width = 10;
  pattern.height = 10;
  
  ctx.fillStyle = hexToRgba(color, 0.15);
  ctx.fillRect(0, 0, pattern.width, pattern.height);
  
  ctx.strokeStyle = hexToRgba(color, 0.3);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(pattern.width, pattern.height);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(pattern.width, 0);
  ctx.lineTo(0, pattern.height);
  ctx.stroke();
  
  const p = ctx.createPattern(pattern, 'repeat');
  patternCache.set(color, p);
  return p;
};

/* --------------------------------------------------
                Chart Options
-------------------------------------------------- */
const getChartOptions = (isRevenue, currentMonthIndex = -1) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { intersect: false },
  plugins: { 
    legend: { 
      display: false,
      labels: { font: { family: 'Geist, sans-serif' } }
    },
    tooltip: {
      filter: (tooltipItem, index, tooltipItems) => {
        const label = tooltipItem.dataset.label || '';
        const value = tooltipItem.parsed.y;
        const dataIndex = tooltipItem.dataIndex;
        
        // Skip null/undefined values
        if (value === null || value === undefined) {
          return false;
        }
        
        // Hide Base dataset (implementation detail, other datasets show the values)
        if (label.includes('(Base)')) {
          return false;
        }
        
        // Hide Estimated dataset except at the current month index (final estimated point)
        // At the transition point (previous month), Estimated is just a connection point, not a real estimate
        if (label.includes('(Estimated)')) {
          if (currentMonthIndex >= 0 && dataIndex !== currentMonthIndex) {
            return false;
          }
        }
        
        // Hide Fill dataset if Solid dataset has a value at the same point
        // (Fill is visual only, Solid shows the actual data line)
        if (label.includes('(Estimated Fill)')) {
          const hasSolid = tooltipItems.some(item => 
            item.dataset.label && 
            !item.dataset.label.includes('(Estimated)') && 
            !item.dataset.label.includes('(Base)') &&
            !item.dataset.label.includes('(Estimated Fill)') &&
            item.parsed.y !== null && 
            item.parsed.y !== undefined
          );
          if (hasSolid) return false;
        }
        
        // Show Solid (actual values), Estimated (only at current month), and Fill (only when Solid doesn't have value)
        return true;
      },
      callbacks: {
        label: (context) => {
          let labelStr = context.dataset.label || '';
          // Simplify label
          if (labelStr.includes('(Estimated)')) {
            labelStr = 'Estimated';
          } else if (labelStr.includes('(Estimated Fill)')) {
            labelStr = 'Actual';
          } else if (labelStr.includes('(Base)')) {
            labelStr = 'Actual';
          } else {
            labelStr = 'Actual';
          }

          const value = context.parsed.y;
          // Skip if value is null/undefined
          if (value === null || value === undefined) {
            return null;
          }
          
          const formattedValue = isRevenue 
            ? '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : value;
            
          return `${labelStr}: ${formattedValue}`;
        }
      }
    }
  },
  animation: { duration: 300 },
  scales: {
    x: { 
      display: true, 
      ticks: { 
        color: '#6b7280',
        font: { family: 'Geist, sans-serif' }
      }, 
      grid: { color: '#e5e7eb' } 
    },
    y: { 
      display: true, 
      ticks: { 
        color: '#6b7280',
        font: { family: 'Geist, sans-serif' },
        callback: (value) => {
          return isRevenue 
            ? '$' + value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
            : value;
        }
      }, 
      grid: { color: '#e5e7eb' } 
    }
  }
});

/* --------------------------------------------------
                Load Event Listener
-------------------------------------------------- */
document.addEventListener('turbo:load', function() {
  if (window.__heartbeatCharts) {
    window.__heartbeatCharts.forEach(chart => chart.destroy());
    window.__heartbeatCharts.clear();
  }

  // Clear pattern cache on reload to ensure clean state
  patternCache.clear();

  // Store for global cleanup
  const charts = new Map();

  const buildDatasets = (label, chartData, chartColor, estimatedData, currentMonthFlags) => {
    const currentMonthIndex = currentMonthFlags ? currentMonthFlags.findIndex(flag => flag === true) : -1;
    const hasEstimate = currentMonthIndex >= 0 && estimatedData && estimatedData[currentMonthIndex] !== null;

    // Common solid dataset if no estimate or estimate is at start
    if (!hasEstimate || currentMonthIndex <= 0) {
      return [{
        label: label,
        data: chartData,
        borderColor: chartColor,
        backgroundColor: hexToRgba(chartColor, 0.1),
        tension: 0.4,
        fill: true
      }];
    }

    // 0. Base Fill (Background under actual values)
    // This ensures no white gap under the actual value of the current month
    const baseDataset = {
      label: `${label} (Base)`,
      data: chartData,
      borderColor: 'transparent',
      backgroundColor: hexToRgba(chartColor, 0.1),
      tension: 0.4,
      fill: 'origin',
      pointRadius: 0,
      pointHoverRadius: 0
    };

    // 1. Solid line up to previous month
    const solidData = chartData.map((value, index) => 
      index <= currentMonthIndex - 1 ? value : null
    );

    const solidDataset = {
      label: label,
      data: solidData,
      borderColor: chartColor,
      backgroundColor: hexToRgba(chartColor, 0.1),
      tension: 0.4,
      fill: false // Handled by Base
    };

    // 2. Dashed line from previous month to estimated point
    const dashedData = chartData.map((value, index) => {
      if (index === currentMonthIndex - 1) return value;
      if (index === currentMonthIndex) return estimatedData[index];
      return null;
    });

    const dashedDataset = {
      label: `${label} (Estimated)`,
      data: dashedData,
      borderColor: chartColor,
      backgroundColor: 'transparent',
      borderDash: [5, 5],
      borderWidth: 2,
      tension: 0.4,
      fill: false,
      pointRadius: (ctx) => ctx.dataIndex === currentMonthIndex ? 4 : 0,
      pointHoverRadius: 6,
      pointBackgroundColor: chartColor,
      pointBorderColor: chartColor
    };

    // 3. Fill area between actual and estimated
    const fillData = chartData.map((value, index) => {
      if (index === currentMonthIndex) return value;
      if (index === currentMonthIndex - 1) return chartData[index];
      return null;
    });

    const fillDataset = {
      label: `${label} (Estimated Fill)`,
      data: fillData,
      borderColor: 'transparent',
      backgroundColor: createStripedPattern(chartColor),
      tension: 0.4,
      fill: '-1', // Fill between this dataset and the previous one (dashed estimated line)
      pointRadius: 0,
      pointHoverRadius: 0
    };

    return [baseDataset, solidDataset, dashedDataset, fillDataset];
  };

  // Create Chart
  const createChart = (canvasId, label) => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;

    try {
      const { months, values, color, estimatedValues, isCurrentMonth } = canvas.dataset;
      if (!months || !values) return;

      const chartLabels = JSON.parse(months);
      const chartData = JSON.parse(values);
      const chartColor = color || '#000000';
      const isRevenue = canvasId === 'downloadRevenueChart';
      
      const estimatedData = estimatedValues ? JSON.parse(estimatedValues) : null;
      const currentMonthFlags = isCurrentMonth ? JSON.parse(isCurrentMonth) : null;
      const currentMonthIndex = currentMonthFlags ? currentMonthFlags.findIndex(flag => flag === true) : -1;

      const datasets = buildDatasets(label, chartData, chartColor, estimatedData, currentMonthFlags);

      const chart = new Chart(canvas, {
        type: 'line',
        data: {
          labels: chartLabels,
          datasets: datasets
        },
        options: getChartOptions(isRevenue, currentMonthIndex)
      });

      charts.set(canvasId, chart);
    } catch (error) {
      console.error(`Error creating chart ${canvasId}:`, error);
    }
  };

  // Initialize Charts
  createChart('downloadRevenueChart', 'Download Revenue');
  createChart('downloadsChart', 'Downloads');
  createChart('projectsChart', 'Projects');
  createChart('customersChart', 'New Customers');
  createChart('leadsChart', 'Leads');

  // Store for global cleanup
  window.__heartbeatCharts = charts;
});

/* --------------------------------------------------
                Destroy Charts on Cache
-------------------------------------------------- */
document.addEventListener('turbo:before-cache', function() {
  if (window.__heartbeatCharts) {
    window.__heartbeatCharts.forEach(chart => chart.destroy());
    window.__heartbeatCharts.clear();
    window.__heartbeatCharts = null;
  }
});
